/**
 * Minimal auth context for the vault app.
 *
 * Tracks the Firebase user (from the SECONDARY project) and exposes the
 * magic-link login primitives. The vault population signs in with an email-keyed
 * flow: the API looks the user up in the vault project and mints a vault custom
 * token (see APP_AND_AUTH_SPLIT_PLAN.md §3.9 / Phase 6), which we exchange here
 * via signInWithCustomToken.
 *
 * `requestMagicLink` targets `POST {API}/vault/auth/request` (send a link) and
 * `verifyMagicLink` targets `POST {API}/vault/auth/verify` (exchange the emailed
 * link for a vault custom token). Both are served by the backend `vault_auth`
 * module and mint tokens against the SECONDARY (vault) project.
 */
import {
  type User,
  onAuthStateChanged,
  signInWithCustomToken,
  signOut,
} from "firebase/auth";
import {
  type ReactNode,
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";

import { auth } from "@/lib/firebase";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:8000";

/** Vault-population role for a single vault. */
export type VaultRole = "owner" | "trustee" | "executor";

/** Bounded owner/heir capability flags derived from the vault user's token. */
export interface VaultCapabilities {
  /** The user owns at least one vault. */
  isOwner: boolean;
  /** The user is a trustee/executor of at least one vault. */
  isHeir: boolean;
}

interface AuthState {
  user: User | null;
  loading: boolean;
  /**
   * Bounded capability flags from the `vault_is_owner` / `vault_is_heir` custom
   * claims. These answer "does this user own any vault" / "is this user a
   * trustee/executor of any vault" — enough to gate the owner-vs-heir UI. The
   * authoritative per-vault role lives in Postgres (`vault.vault_memberships`) and
   * is enforced by the backend on every vault-scoped request. Both false before the
   * token loads or for a user with no vault relationships.
   */
  vaultCapabilities: VaultCapabilities;
  requestMagicLink: (email: string) => Promise<void>;
  /** Consume an emailed magic link (challenge_id + token) and sign in. */
  verifyMagicLink: (challengeId: string, token: string) => Promise<void>;
  /** Exchange a backend-minted vault custom token for a session. */
  loginWithCustomToken: (token: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [vaultCapabilities, setVaultCapabilities] = useState<VaultCapabilities>(
    {
      isOwner: false,
      isHeir: false,
    }
  );

  useEffect(() => {
    return onAuthStateChanged(auth, async (u) => {
      setUser(u);
      if (u) {
        try {
          const { claims } = await u.getIdTokenResult();
          setVaultCapabilities({
            isOwner: claims.vault_is_owner === true,
            isHeir: claims.vault_is_heir === true,
          });
        } catch {
          setVaultCapabilities({ isOwner: false, isHeir: false });
        }
      } else {
        setVaultCapabilities({ isOwner: false, isHeir: false });
      }
      setLoading(false);
    });
  }, []);

  const requestMagicLink = async (email: string) => {
    // Enumeration-safe on the backend: always 204 when the request is well-formed,
    // whether or not the email maps to a vault account.
    const res = await fetch(`${API_URL}/vault/auth/request`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, portal: true }),
    });
    if (!res.ok) {
      const detail = await res.text().catch(() => "");
      throw new Error(
        `Could not send sign-in link (${res.status}).${detail ? ` ${detail}` : ""}`
      );
    }
  };

  const verifyMagicLink = async (challengeId: string, token: string) => {
    const res = await fetch(`${API_URL}/vault/auth/verify`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ challenge_id: challengeId, token }),
    });
    if (!res.ok) {
      let message = `Sign-in link is invalid or expired (${res.status}).`;
      try {
        const body = (await res.json()) as { error?: string };
        if (body?.error) message = body.error;
      } catch {
        /* non-JSON error body — keep the default message */
      }
      throw new Error(message);
    }
    const { custom_token } = (await res.json()) as { custom_token: string };
    await signInWithCustomToken(auth, custom_token);
  };

  const loginWithCustomToken = async (token: string) => {
    await signInWithCustomToken(auth, token);
  };

  const logout = async () => {
    await signOut(auth);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        vaultCapabilities,
        requestMagicLink,
        verifyMagicLink,
        loginWithCustomToken,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}
